import React from "react";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import { formFields, hrEnums } from "../../Constant/HR_Staff/hrEnums";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import { digitRegex } from "../../Constant/validation";
import { extractDate, isRequired } from "../../Constant/common";
import { ProfileSection, InfoField } from "../../BaseComponents/ProfileSection";
import { Briefcase } from "react-feather";
import DocumentUploadSection from "./DocumentUploadSection";
import {
  parseIdentifyDocument,
  migrateLegacyDocument,
} from "../../Constant/HR_Staff/documentHelpers";
import { MANDATORY_DOCUMENT_TYPES, MANDATORY_DOC_VALUES } from "../../Constant/HR_Staff/hrEnums";

/**
 * Build the documents array for view mode from the API response.
 * API returns: data.aadhaar_card (string) + data.identify_document (object)
 */
const buildViewDocuments = (staffData) => {
  const mandatorySeeds = MANDATORY_DOCUMENT_TYPES.map((m) => ({
    document_type: m.value,
    files: [{ file_name: "" }],
    mandatory: true,
  }));

  // Aadhaar from root
  const aadhaarFile = staffData?.aadhaar_card;

  // Optional docs from identify_document at root
  const identifyDoc = staffData?.identify_document;
  let optional = [];
  if (identifyDoc && typeof identifyDoc === "object" && Object.keys(identifyDoc).length > 0) {
    optional = parseIdentifyDocument(identifyDoc);
  } else {
    // Legacy fallback
    const legacy = staffData?.staffGeneralInformation?.other_document;
    if (legacy) optional = migrateLegacyDocument(legacy);
  }

  // Merge mandatory seeds with actual data
  const merged = mandatorySeeds.map((seed) => {
    if (seed.document_type === "aadhaar_card" && aadhaarFile) {
      return { ...seed, files: [{ file_name: aadhaarFile }] };
    }
    const found = optional.find((e) => e.document_type === seed.document_type);
    return found ? { ...found, mandatory: true } : seed;
  });

  const optionalDocs = optional.filter((e) => !MANDATORY_DOC_VALUES.has(e.document_type));
  return [...merged, ...optionalDocs];
};

const EmploymentDetails = ({
  staffForm,
  viewEmploymentMode,
  handleEmploymentEdit,
  handleEmploymentDiscard,
  editEmploymentMode,
  staffData,
  btnLoader,
  isEditMode,
  documents,
  onDocumentAdd,
  onDocumentRemove,
  onDocumentTypeChange,
  onDocumentFileChange,
  onDocumentFileAdd,
  onDocumentFileRemove,
  uploadingIndex,
  documentErrors,
  documentTouched,
  isProfile,
}) => {
  const { values, initialValues } = staffForm;

  const isSaveDisabled = React.useMemo(() => {
    const fields = [
      "joining_date",
      "uan",
      "pan",
      "aadhar_number",
      "monthly_salary",
      "monthly_special_allowance",
      "documents",
    ];

    const normalizeDate = (d) => {
      if (!d) return "";
      if (d instanceof Date) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
      const match = String(d).match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);
      if (match) return `${match[1]}-${match[2]}-${match[3]}`;
      const date = new Date(d);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    return !fields.some((field) => {
      const val = values[field];
      const initVal = initialValues[field];

      if (field === "joining_date") {
        return normalizeDate(val) !== normalizeDate(initVal);
      }

      if (field === "documents") {
        const valArr = Array.isArray(val) ? val : [];
        const initArr = Array.isArray(initVal) ? initVal : [];
        if (valArr.length !== initArr.length) return true;
        return valArr.some((doc, idx) => {
          const initDoc = initArr[idx];
          if (!initDoc) return true;
          if (doc.document_type !== initDoc.document_type) return true;
          const docFiles = Array.isArray(doc.files) ? doc.files : [];
          const initFiles = Array.isArray(initDoc.files) ? initDoc.files : [];
          if (docFiles.length !== initFiles.length) return true;
          return docFiles.some((f, fIdx) => f.file_name !== initFiles[fIdx]?.file_name);
        });
      }

      const normalizedVal = val === null || val === undefined ? "" : String(val).trim();
      const normalizedInitVal =
        initVal === null || initVal === undefined ? "" : String(initVal).trim();
      return normalizedVal !== normalizedInitVal;
    });
  }, [values, initialValues]);

  return (
    <ProfileSection
      id="employment-info"
      title={hrEnums.EMPLOYMENT_INFO}
      icon={Briefcase}
      iconColor="#10b981"
      viewMode={viewEmploymentMode}
      editMode={editEmploymentMode}
      onEdit={handleEmploymentEdit}
      onDiscard={handleEmploymentDiscard}
      isEditable={true}
      btnLoader={btnLoader}
      animDelay="120ms"
      isSaveDisabled={isSaveDisabled}
    >
      {viewEmploymentMode ? (
        <div className="row">
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.JOINING_DATE}
              value={
                staffData?.staffGeneralInformation?.date_of_joining
                  ? extractDate(staffData.staffGeneralInformation.date_of_joining)
                  : null
              }
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.UAN}
              value={staffData?.staffGeneralInformation?.uan_no}
              mono
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.PAN}
              value={staffData?.staffGeneralInformation?.pan_no}
              mono
            />
          </div>
          <div className="col-6 col-md-3">
            <InfoField
              label={hrEnums.AADHAR_NUMBER}
              value={staffData?.staffGeneralInformation?.aadhar_no}
              mono
            />
          </div>
          <div className="col-12">
            <DocumentUploadSection
              viewMode={true}
              isProfile={isProfile}
              documents={buildViewDocuments(staffData)}
              onAdd={() => {}}
              onRemove={() => {}}
              onTypeChange={() => {}}
              onFileChange={() => {}}
              onFileAdd={() => {}}
              onFileRemove={() => {}}
              uploadingIndex={null}
              errors={[]}
              touched={[]}
            />
          </div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseDatePicker
              label={
                <>
                  {hrEnums.JOINING_DATE}
                  {isRequired}
                </>
              }
              name={formFields.joining_date}
              placeholder={PlaceHolderFormat(hrEnums.JOINING_DATE)}
              value={staffForm.values.joining_date}
              touched={staffForm.touched.joining_date}
              error={staffForm.errors.joining_date}
              onBlur={staffForm.handleBlur}
              onChange={(v) => staffForm.setFieldValue(formFields.joining_date, v)}
              disabled={btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              name={formFields.uan}
              type="text"
              label={hrEnums.UAN}
              placeholder={PlaceHolderFormat(hrEnums.UAN)}
              value={staffForm.values.uan}
              touched={staffForm.touched.uan}
              error={staffForm.errors.uan}
              handleBlur={staffForm.handleBlur}
              maxLength={12}
              handleChange={(e) => {
                if (digitRegex.test(e?.target?.value)) staffForm.handleChange(e);
              }}
              disabled={btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              name={formFields.pan}
              type="text"
              label={hrEnums.PAN}
              placeholder={PlaceHolderFormat(hrEnums.PAN)}
              value={staffForm.values.pan}
              touched={staffForm.touched.pan}
              error={staffForm.errors.pan}
              handleBlur={staffForm.handleBlur}
              maxLength={10}
              handleChange={staffForm.handleChange}
              disabled={btnLoader}
            />
          </div>
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <BaseInput
              name={formFields.aadhar_number}
              type="text"
              label={hrEnums.AADHAR_NUMBER}
              placeholder={PlaceHolderFormat(hrEnums.AADHAR_NUMBER)}
              value={staffForm.values.aadhar_number}
              touched={staffForm.touched.aadhar_number}
              error={staffForm.errors.aadhar_number}
              handleBlur={staffForm.handleBlur}
              maxLength={12}
              handleChange={(e) => {
                if (digitRegex.test(e?.target?.value)) staffForm.handleChange(e);
              }}
              disabled={btnLoader}
            />
          </div>
          {!isEditMode && (
            <>
              <div className="col-xl-3 col-lg-3 col-md-6 col-12">
                <BaseInput
                  name={formFields.monthly_salary}
                  type="number"
                  label={
                    <>
                      {hrEnums.MONTHLY_SALARY_INR}
                      {isRequired}
                    </>
                  }
                  placeholder={PlaceHolderFormat(hrEnums.MONTHLY_SALARY)}
                  value={staffForm.values.monthly_salary}
                  touched={staffForm.touched.monthly_salary}
                  error={staffForm.errors.monthly_salary}
                  handleBlur={staffForm.handleBlur}
                  handleChange={(e) => {
                    if (digitRegex.test(e?.target?.value)) staffForm.handleChange(e);
                  }}
                  disabled={btnLoader}
                />
              </div>
              <div className="col-xl-3 col-lg-3 col-md-6 col-12">
                <BaseInput
                  name={formFields.monthly_special_allowance}
                  type="number"
                  label={hrEnums.MONTHLY_ALLOWANCE_INR}
                  placeholder={PlaceHolderFormat(hrEnums.MONTHLY_ALLOWANCE)}
                  value={staffForm.values.monthly_special_allowance}
                  touched={staffForm.touched.monthly_special_allowance}
                  error={staffForm.errors.monthly_special_allowance}
                  handleBlur={staffForm.handleBlur}
                  handleChange={(e) => {
                    if (digitRegex.test(e?.target?.value)) staffForm.handleChange(e);
                  }}
                  disabled={btnLoader}
                />
              </div>
            </>
          )}
          <div className="col-12">
            <DocumentUploadSection
              viewMode={false}
              isProfile={isProfile}
              documents={documents}
              onAdd={onDocumentAdd}
              onRemove={onDocumentRemove}
              onTypeChange={onDocumentTypeChange}
              onFileChange={onDocumentFileChange}
              onFileAdd={onDocumentFileAdd}
              onFileRemove={onDocumentFileRemove}
              uploadingIndex={uploadingIndex}
              errors={documentErrors}
              touched={documentTouched}
              disabled={btnLoader}
            />
          </div>
        </div>
      )}
    </ProfileSection>
  );
};

export default EmploymentDetails;
